import { AddEditRankingFormSchema } from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRanking.constants';
import { CUSTOMER_TEAMS } from 'constants/endpoints';
import { useFormBehaviors } from 'hooks';
import {
  UseFormBehaviors,
  UseFormBehaviorsEventType,
  UseFormBehaviors_Options,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { FC, createContext, useCallback, useContext, useMemo } from 'react';
import { toastService } from 'services';
import i18next from 'i18next';
import { API_POST_editRankingCSV } from 'requests/POST_EditRankingCSV';
import {
  inviteTeaMemberCSVModalRoutingService,
  useInviteTeamMembersByCSVModalUIContext,
} from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import { CsvError } from 'components/InviteTeamMembersByCSVModal/views/ImportViaSpreadsheet/TeamMemberCSVView.constants';
import { METHOD_GET } from 'requests';
import { saveResponseAsCSV } from 'utils/saveResponseAsCSV';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';

type TeamMemberEditRankingFormContextType = UseFormBehaviors<AddEditRankingFormSchema> & {
  onDownload: () => {};
};

const VALIDATION_CSV = (file: File | undefined) => {
  if (!file) {
    return i18next.t('team_member:FILE_IS_REQUIRED');
  }
  const allowedFileTypes = ['csv']; // Add your allowed file types here
  const fileNameParts = file.name.split('.');
  const fileExtension = fileNameParts[fileNameParts.length - 1].toLowerCase();
  if (!allowedFileTypes.includes(fileExtension)) {
    return i18next.t('team_member:FILE_NOT_CORRECT_FORMAT_UPLOAD_CSV_FILE_AND_TRY_AGAIN');
  }
  return '';
};

const TeamMemberEditRankingInitialState = {
  csv: undefined,
  location_ids: [],
};

export const TeamMemberEditRankingFormContext = createContext<TeamMemberEditRankingFormContextType>(
  {} as TeamMemberEditRankingFormContextType,
);

export const TeamMemberEditRankingFormProvider: FC = ({ children }) => {
  const { apiRequest } = useAPIActions();
  const { goToViewError } = useInviteTeamMembersByCSVModalUIContext();
  const handleSubmit = async ({ csv }: AddEditRankingFormSchema) => {
    let collectedErrors: CsvError[] = [];
    const formData = new FormData();
    formData.append('csv[upload]', csv as Blob);
    if (csv) {
      apiRequest(API_POST_editRankingCSV({ formData }), {
        onSuccess: () => {
          toastService.success(i18next.t('team_member:RANKING_SUCCESSFULLY_IMPORTED'));
          inviteTeaMemberCSVModalRoutingService.close();
        },
        onError: (error) => {
          const errors = error?.data;
          if (errors && Array.isArray(errors)) {
            errors.forEach((errorObj) => {
              collectedErrors.push(errorObj);
            });
          }
          goToViewError({ csvErrors: collectedErrors });
        },
      });
    }
  };

  const addEditRankingRuleFormBehaviorsOptions: UseFormBehaviors_Options<AddEditRankingFormSchema> =
    {
      initialState: { ...TeamMemberEditRankingInitialState },
      isDirtyCheckEnabled: true,
      type: 'CREATE',
      onSubmit: handleSubmit,
      validations: {
        csv: VALIDATION_CSV,
      },
    };

  const addEditRankingRuleForm = useFormBehaviors<AddEditRankingFormSchema>(
    addEditRankingRuleFormBehaviorsOptions,
  );
  const onDownload = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_GET,
          responseType: 'blob',
          url: `${CUSTOMER_TEAMS}/csv/export`,
          query: {
            csv: { filter: { with_primary_community: addEditRankingRuleForm.values.location_ids } },
          },
        },
        {
          onSuccess: (response) => {
            saveResponseAsCSV(response, `seniority_ranking-${Date.now()}.csv`);
          },
          onError: handleGenericErrorMessage,
        },
      ),
    [addEditRankingRuleForm.values.location_ids, apiRequest],
  );

  const value = useMemo(
    () => ({
      ...addEditRankingRuleForm,
      onChange: (e: UseFormBehaviorsEventType | UseFormBehaviorsEventType[]) => {
        if (Array.isArray(e)) {
          // If e is an array of events, iterate through them
          e.forEach(({ target: { value = '', name = 'csv', ...rest } }) => {
            addEditRankingRuleForm.onChange({ target: { value, name, ...rest } });
          });
        } else {
          const {
            target: { value = '', name = 'csv', ...rest },
          } = e;
          addEditRankingRuleForm.onChange({
            target: { name, value, ...rest },
          });
        }
      },
      onDownload,
      isDisabled: !addEditRankingRuleForm.values.location_ids.length,
    }),
    [addEditRankingRuleForm, onDownload],
  );
  return (
    <TeamMemberEditRankingFormContext.Provider value={value}>
      {children}
    </TeamMemberEditRankingFormContext.Provider>
  );
};

export const useTeamMemberEditRankingFormContext = (): TeamMemberEditRankingFormContextType =>
  useContext(TeamMemberEditRankingFormContext);
